import { useState, useEffect, useMemo, useRef } from 'react';
import {
  useServiceTypes,
  useServiceType,
  useCreateServiceType,
  useUpdateServiceType,
  useDeleteServiceType,
  useBulkDeleteServiceTypes,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { generateSnakeCode, makeUniqueSnakeCode } from '../../utils/code';
import { toast } from 'sonner';
import {
  Tags,
  Plus,
  Search,
  X,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Save,
  Hash,
  Edit3,
} from 'lucide-react';
import type { ServiceType } from '../../services/httpServices/serviceService';

interface ServiceTypeFormState {
  code: string;
  name: string;
  displayOrder: number;
  status: 'ACTIVE' | 'INACTIVE';
}

const EMPTY_FORM: ServiceTypeFormState = {
  code: '',
  name: '',
  displayOrder: 0,
  status: 'ACTIVE',
};

export function ServiceTypes() {
  const limit = 20;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceType | null>(null);
  const [confirm, setConfirm] = useState<ServiceType | null>(null);
  const [form, setForm] = useState<ServiceTypeFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const { data, isLoading, error } = useServiceTypes(page, limit, debouncedSearch || undefined);
  // Every service-type code (active + inactive), used to keep an auto-generated
  // code unique.
  const { data: allTypesData } = useServiceTypes(1, 1000);
  const { data: viewData, isLoading: viewLoading, error: viewError } = useServiceType(viewId || undefined);
  const createMutation = useCreateServiceType();
  const updateMutation = useUpdateServiceType();
  const deleteMutation = useDeleteServiceType();
  const bulkDeleteMutation = useBulkDeleteServiceTypes();

  const serviceTypes = data?.data || [];
  const meta = data?.meta;
  const viewedType = viewData?.data;
  const existingCodes = useMemo(
    () => (allTypesData?.data || []).map((type: ServiceType) => type.code).filter(Boolean) as string[],
    [allTypesData],
  );

  // Auto-generate the code from the name (snake_case), kept unique among
  // existing types and left editable — editing the code stops auto-generation.
  useEffect(() => {
    if (editing) return;
    if (!form.name.trim()) return;
    if (codeManuallyEdited) return;
    const base = generateSnakeCode(form.name, 50);
    if (!base) return;
    const unique = makeUniqueSnakeCode(base, existingCodes, { maxLength: 50 });
    setForm((prev) => (prev.code === unique ? prev : { ...prev, code: unique }));
  }, [form.name, codeManuallyEdited, editing, existingCodes]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(serviceTypes.map((t: ServiceType) => t.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [serviceTypes]);

  const allSelected = serviceTypes.length > 0 && serviceTypes.every((t: ServiceType) => selectedIds.has(t.id));
  const someSelected = serviceTypes.some((t: ServiceType) => selectedIds.has(t.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = serviceTypes.length > 0 && serviceTypes.every((t: ServiceType) => prev.has(t.id));
      return everySelected ? new Set() : new Set(serviceTypes.map((t: ServiceType) => t.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setCodeManuallyEdited(false);
    setFormOpen(true);
  };

  const openEdit = (serviceType: ServiceType) => {
    setEditing(serviceType);
    setForm({
      code: serviceType.code,
      name: serviceType.name,
      displayOrder: serviceType.displayOrder || 0,
      status: serviceType.status || 'ACTIVE',
    });
    setFormErrors({});
    // Editing an existing type keeps its code as-is; only new types auto-generate.
    setCodeManuallyEdited(true);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setFormErrors({});
  };

  const updateField = (field: keyof ServiceTypeFormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'code') {
      setCodeManuallyEdited(String(value).trim() !== '');
    }
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!form.code.trim()) errors.code = 'Code is required';
    if (!form.name.trim()) errors.name = 'Name is required';
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, data: form });
        toast.success('Service type updated');
      } else {
        await createMutation.mutateAsync(form);
        toast.success('Service type created');
      }
      closeForm();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await deleteMutation.mutateAsync(confirm.id);
      toast.success('Service type deleted');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete service type');
    } finally {
      setConfirm(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteMutation.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} service type${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some service types');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const inputBase = 'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-colors';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const labelClass = 'block text-sm font-medium text-text mb-1';
  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tags className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Service Types</h1>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add Service Type
        </button>
      </div>

      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search service types..."
            className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={serviceTypes.length === 0}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    title="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" /> Failed to load service types
                  </td>
                </tr>
              ) : serviceTypes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-text-muted">
                    {search ? (
                      <span>
                        No service types match your search.{' '}
                        <button onClick={() => setSearch('')} className="text-primary hover:underline">
                          Clear search
                        </button>
                      </span>
                    ) : (
                      'No service types found. Add your first service type to get started.'
                    )}
                  </td>
                </tr>
              ) : (
                serviceTypes.map((serviceType: ServiceType) => (
                  <tr key={serviceType.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(serviceType.id)}
                        onChange={() => toggleOne(serviceType.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-text">
                      <button
                        onClick={() => setViewId(serviceType.id)}
                        className="hover:text-primary transition-colors text-left cursor-pointer"
                        title="View details"
                      >
                        {serviceType.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono text-text-muted">{serviceType.code}</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          serviceType.status === 'ACTIVE' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {serviceType.status === 'ACTIVE' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {serviceType.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(serviceType)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setConfirm(serviceType)}
                          disabled={deleteMutation.isPending}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="service types"
        />
      </div>

      {/* View Details modal */}
      {viewId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setViewId(null)}
        >
          <div
            className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <Tags className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-text">Service Type Details</h2>
              </div>
              <button
                onClick={() => setViewId(null)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {viewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : viewError || !viewedType ? (
              <div className="flex items-center gap-2 p-4 m-5 bg-danger/10 border border-danger/20 rounded-lg text-danger">
                <AlertCircle size={18} />
                <span>Failed to load service type details. Please try again.</span>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-xl font-bold text-text">{viewedType.name}</h3>
                    <p className="text-sm text-text-muted font-mono mt-0.5">{viewedType.code}</p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                      viewedType.status === 'ACTIVE'
                        ? 'bg-success/10 text-success'
                        : 'bg-danger/10 text-danger'
                    }`}
                  >
                    {viewedType.status === 'ACTIVE' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    {viewedType.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Hash size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Code</p>
                      <p className="text-sm font-medium text-text font-mono">{viewedType.code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Hash size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-text-muted">Display Order</p>
                      <p className="text-sm font-medium text-text">{viewedType.displayOrder ?? 0}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end pt-2 border-t border-border">
                  <button
                    onClick={() => {
                      const target = viewedType;
                      setViewId(null);
                      openEdit(target as ServiceType);
                    }}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <Edit3 size={16} />
                    Edit Service Type
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-bold text-text">
                {editing ? 'Edit Service Type' : 'Add Service Type'}
              </h2>
              <button
                onClick={closeForm}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {formErrors.general && (
                <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{formErrors.general}</span>
                </div>
              )}

              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  className={ic('name')}
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Consultation"
                />
                {formErrors.name && <p className="text-xs text-danger mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <label className={labelClass}>
                  Code * {!editing && <span className="text-text-muted font-normal">(auto-generated)</span>}
                </label>
                <input
                  type="text"
                  className={`${ic('code')} font-mono`}
                  value={form.code}
                  onChange={(e) => updateField('code', e.target.value)}
                  placeholder="consultation"
                />
                {formErrors.code && <p className="text-xs text-danger mt-1">{formErrors.code}</p>}
                <p className="text-[11px] text-text-muted mt-1">
                  Stored on the service and used by billing. Lower-case with underscores (e.g. lab_test).
                </p>
              </div>

              <div>
                <label className={labelClass}>Display Order</label>
                <input
                  type="number"
                  className={`${inputBase} ${inputValid}`}
                  value={form.displayOrder}
                  onChange={(e) => updateField('displayOrder', Number(e.target.value))}
                  min={0}
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.status === 'ACTIVE'}
                    onChange={(e) => updateField('status', e.target.checked ? 'ACTIVE' : 'INACTIVE')}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                  />
                  <span className="text-sm text-text">{form.status === 'ACTIVE' ? 'Active' : 'Inactive'}</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 size={16} className="animate-spin" />
                  )}
                  <Save size={16} />
                  {editing ? 'Save Changes' : 'Add Service Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirm !== null}
        title="Delete service type"
        description={
          <>
            Delete <strong>{confirm?.name}</strong>? Services that reference it keep their record but lose
            this type association.
          </>
        }
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} service type${selectedIds.size === 1 ? '' : 's'}?`}
        description="Services that reference the selected types keep their record but lose the type association. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
